import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Package, Tag, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminUsers = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pending');

  const fetchUsers = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/all-users`, { withCredentials: true })
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId, approve) => {
    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/approve`,
        { approved: approve },
        { withCredentials: true }
      );
      toast.success(approve ? 'Vendedor aprovado!' : 'Vendedor rejeitado');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao processar solicitação');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-users-title">
            Gerenciar Usuários
          </h1>
          <p className="text-lg text-gray-600">Aprove ou rejeite vendedores</p>
        </div>

        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setView('pending')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'pending'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-pending-btn"
            >
              Pendentes ({pendingUsers.length})
            </button>
            <button
              onClick={() => setView('all')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                view === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-all-btn"
            >
              Todos ({allUsers.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {view === 'pending' ? (
              pendingUsers.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg text-gray-600">Nenhum vendedor pendente de aprovação</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {pendingUsers.map((user) => (
                    <div key={user.user_id} className="p-6 hover:bg-gray-50 transition-colors" data-testid={`pending-user-${user.user_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-lg">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-amber-600 font-medium mt-1">Vendedor - Aguardando aprovação</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleApprove(user.user_id, true)}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            data-testid={`approve-btn-${user.user_id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Aprovar</span>
                          </button>
                          <button
                            onClick={() => handleApprove(user.user_id, false)}
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            data-testid={`reject-btn-${user.user_id}`}
                          >
                            <XCircle className="w-4 h-4" />
                            <span>Rejeitar</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="divide-y divide-gray-200">
                {allUsers.map((user) => (
                  <div key={user.user_id} className="p-6 hover:bg-gray-50 transition-colors" data-testid={`user-${user.user_id}`}>
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        user.role === 'admin' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                        user.role === 'seller' ? 'bg-gradient-to-br from-indigo-500 to-blue-500' :
                        'bg-gradient-to-br from-green-500 to-emerald-500'
                      }`}>
                        <span className="text-white font-bold text-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{user.name}</h3>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            user.role === 'seller' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {user.role === 'admin' ? 'Admin' : user.role === 'seller' ? 'Vendedor' : 'Cliente'}
                          </span>
                          {user.role === 'seller' && (
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              user.approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {user.approved ? 'Aprovado' : 'Pendente'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};